import { readSse, versionOf, type SseOutcome } from './sse.js';

/**
 * **API クライアント**（M15 で実装したサーバと 1 対 1）。
 *
 * ## SSE は演出、真実は DynamoDB（§9）
 *
 * **`done` を受け取れなくても、サーバ側のターンは完了している可能性がある。**
 * **この層は「届かなかった」を隠さない。**
 *
 * ## `version` を持ち回る（楽観的ロック）
 *
 * `POST /api/turn` は **`expectedVersion` が食い違うと `duplicate`** を返す。
 * **M15 の実測で 0.20 秒・`Billed Duration` 32ms で弾かれた**
 * （**LLM を呼ぶ前**に弾いているので課金されない）。
 */

export interface ChatMessage {
  readonly id: string;
  readonly role: 'learner' | 'assistant';
  readonly text: string;
  readonly actType?: string;
}

export interface PanelRow {
  readonly id: string;
  readonly displayLabel: string;
  readonly status: 'sufficient' | 'insufficient' | 'unknown';
}

export interface StreamHandlers {
  readonly onUtterance: (text: string, actType: string | undefined) => void;
  /**
   * **理解状態パネルの更新**（D11。M16 で配線した）。
   *
   * **サーバが `conceptKey` 昇順で並べたものをそのまま入れる**（D121。UI は並べ替えない）。
   */
  readonly onState: (rows: readonly PanelRow[]) => void;
  /**
   * **いま何をしているか**（D132）。
   *
   * **`label` はサーバが引いた固定語彙**（`@ua/domain` の `BOOTSTRAP_PHASES`）。
   * **UI 側で言い換えない** — 言い換えると工程名が 2 か所で定義される
   * （`PhaseIndicator` の規約）。
   */
  readonly onPhase: (label: string) => void;
  readonly onGraphReady: (sessionId: string, labels: readonly string[]) => void;
  readonly onDegraded: (detail: string) => void;
  readonly onUsage: (usage: unknown) => void;
  readonly onDone: (version: number, phase: string) => void;
  readonly onError: (reason: string, currentVersion?: number) => void;
}

/** イベントの取り回しを 1 か所に集める（**画面ごとに分岐を書かない**） */
function dispatch(type: string, data: unknown, id: string | undefined, h: StreamHandlers): void {
  const d = (data ?? {}) as Record<string, unknown>;
  switch (type) {
    case 'utterance':
      h.onUtterance(String(d['text'] ?? ''), d['actType'] as string | undefined);
      break;
    case 'state':
      h.onState(((d['requirements'] as PanelRow[] | undefined) ?? []));
      break;
    case 'phase':
      h.onPhase(String(d['label'] ?? ''));
      break;
    case 'graph_ready':
      h.onGraphReady(String(d['sessionId'] ?? ''), (d['requirementLabels'] as string[]) ?? []);
      break;
    case 'degraded':
      h.onDegraded(String(d['detail'] ?? ''));
      break;
    case 'usage':
      h.onUsage(d);
      break;
    case 'done':
      h.onDone(Number(d['version'] ?? versionOf(id) ?? 0), String(d['phase'] ?? ''));
      break;
    case 'error':
      h.onError(String(d['reason'] ?? 'unknown'), d['currentVersion'] as number | undefined);
      break;
    default:
      /**
       * **知らないイベントを黙って捨てない。**
       * サーバが新しい種類を足したのにクライアントが古い、という状態は
       * **「何も起きていないように見える」**という最悪の形で出る。
       */
      h.onDegraded(`未知のイベント: ${type}`);
  }
}

export interface ApiConfig {
  /** **同一オリジン**（CloudFront が `/api/*` を Lambda へ回す）。M16 では相対で足りる */
  readonly baseUrl: string;
  readonly idToken: string;
}

export async function createSession(
  cfg: ApiConfig,
  input: { readonly problemId: string; readonly problemText: string; readonly utterance: string },
  h: StreamHandlers,
): Promise<SseOutcome> {
  return readSse(
    { url: `${cfg.baseUrl}/api/sessions`, idToken: cfg.idToken, body: input },
    (ev) => dispatch(ev.type, ev.data, ev.id, h),
  );
}

export async function sendTurn(
  cfg: ApiConfig,
  input: {
    readonly sessionId: string;
    readonly utterance: string;
    /** **クライアントが見ている版**（食い違えば `duplicate`） */
    readonly expectedVersion: number;
  },
  h: StreamHandlers,
): Promise<SseOutcome> {
  return readSse(
    { url: `${cfg.baseUrl}/api/turn`, idToken: cfg.idToken, body: input },
    (ev) => dispatch(ev.type, ev.data, ev.id, h),
  );
}

export interface SessionSummary {
  readonly sessionId: string;
  readonly problemId: string;
  /**
   * **一覧の見出し**（M16。サーバが問題文の 1 行目を切ったもの。**要約ではない** = D12）。
   * **M16 より前に作られたセッションは持たない。**
   */
  readonly title?: string;
  readonly createdAt: string;
  readonly hiddenByUser?: boolean;
}

export async function listSessions(cfg: ApiConfig): Promise<readonly SessionSummary[]> {
  const res = await fetch(`${cfg.baseUrl}/api/sessions`, {
    headers: { authorization: `Bearer ${cfg.idToken}` },
  });
  if (!res.ok) return [];
  const json = (await res.json()) as { sessions?: SessionSummary[] };
  return json.sessions ?? [];
}

/**
 * **セッションを読み直す**（M16。D12 の「中断して戻れる」/ 切断からの回復）。
 *
 * **SSE は演出、真実は DynamoDB**（§9）なので、
 * **中断からの復帰も、切断からの回復も、この 1 本で足りる**。
 */
export interface SessionDetail {
  readonly sessionId: string;
  readonly version: number;
  readonly phase: string;
  readonly problemText: string;
  /**
   * **ラベルと状態**（M16 で状態を足した）。
   * **これが無いと、開き直したときにパネルが全件「未確認」に戻る** —
   * 会話は復元できているのに理解だけ巻き戻って見える。
   */
  readonly requirements: readonly PanelRow[];
  readonly conversation: readonly { readonly role: string; readonly text: string }[];
}

export async function readSession(cfg: ApiConfig, sessionId: string): Promise<SessionDetail | undefined> {
  const res = await fetch(`${cfg.baseUrl}/api/sessions/${encodeURIComponent(sessionId)}`, {
    headers: { authorization: `Bearer ${cfg.idToken}` },
  });
  if (!res.ok) return undefined;
  return (await res.json()) as SessionDetail;
}
